/* A browser-local wishlist shared by the Shopify theme and the design preview. */
(() => {
  'use strict';
  if (window.celliniWishlistReady) return;
  window.celliniWishlistReady = true;

  const key = 'cellini:wishlist:v1';
  const limit = 200;
  const lang = document.documentElement.lang === 'ar' ? 'ar' : 'en';
  const labels = lang === 'ar'
    ? { remove: 'إزالة من المفضلة', view: 'عرض المنتج', saved: 'أضيف إلى المفضلة', deleted: 'أزيل من المفضلة' }
    : { remove: 'Remove from wishlist', view: 'View product', saved: 'Added to wishlist', deleted: 'Removed from wishlist' };
  const validHandle = value => /^[a-z0-9][a-z0-9-]{0,200}$/.test(value || '');
  const brief = (value, max = 240) => String(value || '').slice(0, max);
  const imageSource = value => {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.origin === location.origin || (url.protocol === 'https:' && (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.net')))) return url.href;
    } catch (_) { /* A missing photo gets a branded placeholder. */ }
    return '';
  };
  const clean = value => {
    if (!value || !validHandle(value.handle)) return null;
    const ratio = Number(value.ratio);
    return {
      handle: value.handle,
      title: brief(value.title),
      type: brief(value.type, 100),
      color: brief(value.color, 100),
      image: imageSource(value.image),
      ratio: Number.isFinite(ratio) && ratio >= .2 && ratio <= 5 ? ratio : 2 / 3,
      price: brief(value.price, 80),
      url: brief(value.url, 500),
      locale: value.locale === 'ar' || value.locale === 'en' ? value.locale : '',
    };
  };
  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(raw) ? raw.slice(0, limit).map(clean).filter(Boolean) : [];
    } catch (_) { return []; }
  };
  let items = read();
  const localizedItems = new Map();
  const localeAttempts = new Set();
  const localeQueue = [];
  let activeLocaleRequests = 0;
  const save = () => {
    try { localStorage.setItem(key, JSON.stringify(items)); } catch (_) { /* Keep this tab usable when storage is blocked. */ }
  };
  const status = document.createElement('span');
  status.className = 'visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const saved = handle => items.some(item => item.handle === handle);
  const buttonRecord = button => clean({
    handle: button.dataset.wishlistHandle,
    title: button.dataset.wishlistTitle,
    type: button.dataset.wishlistType,
    color: button.dataset.wishlistColor,
    image: button.dataset.wishlistImage,
    ratio: button.dataset.wishlistRatio,
    price: button.dataset.wishlistPrice,
    url: button.dataset.wishlistUrl,
    locale: lang,
  });
  const renderControls = () => {
    for (const button of document.querySelectorAll('[data-wishlist-toggle]')) {
      const isSaved = saved(button.dataset.wishlistHandle);
      const label = isSaved ? button.dataset.wishlistRemoveLabel : button.dataset.wishlistSaveLabel;
      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', label || (isSaved ? labels.remove : labels.saved));
      button.title = label || (isSaved ? labels.remove : labels.saved);
      const visible = button.querySelector('.cellini-wishlist-toggle__label');
      if (visible) visible.textContent = isSaved ? (lang === 'ar' ? 'أضيف إلى المفضلة' : 'Saved to wishlist') : (lang === 'ar' ? 'أضف إلى المفضلة' : 'Save to wishlist');
    }
    for (const count of document.querySelectorAll('[data-wishlist-count]')) {
      count.textContent = items.length > 99 ? '99+' : new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-EG').format(items.length);
      count.hidden = items.length === 0;
    }
  };
  const productURL = (page, item) => {
    const prefix = page.dataset.productsUrlPrefix;
    if (prefix) return `${prefix}${encodeURIComponent(item.handle)}${page.dataset.productsUrlSuffix || ''}`;
    try {
      const url = new URL(item.url, location.origin);
      return url.origin === location.origin ? url.href : '#';
    } catch (_) { return '#'; }
  };
  const pumpLocaleQueue = () => {
    while (activeLocaleRequests < 4 && localeQueue.length) {
      const { page, item } = localeQueue.shift();
      activeLocaleRequests += 1;
      fetch(productURL(page, item), { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Product unavailable');
          return response.text();
        })
        .then(html => {
          const documentCopy = new DOMParser().parseFromString(html, 'text/html');
          const button = documentCopy.querySelector(`[data-wishlist-toggle][data-wishlist-handle="${item.handle}"]`);
          if (!button) return;
          const localized = clean({
            ...item,
            title: button.dataset.wishlistTitle || item.title,
            type: button.dataset.wishlistType || item.type,
            color: button.dataset.wishlistColor || item.color,
            price: button.dataset.wishlistPrice || item.price,
            image: button.dataset.wishlistImage || item.image,
            ratio: button.dataset.wishlistRatio || item.ratio,
            locale: lang,
          });
          if (!localized || !items.some(current => current.handle === item.handle)) return;
          localizedItems.set(item.handle, localized);
          render();
        })
        .catch(() => { /* Keep the saved product visible if its source page is unavailable. */ })
        .finally(() => { activeLocaleRequests -= 1; pumpLocaleQueue(); });
    }
  };
  const queueLocale = (page, item) => {
    if (item.locale === lang || localizedItems.has(item.handle) || localeAttempts.has(item.handle)) return;
    localeAttempts.add(item.handle);
    localeQueue.push({ page, item });
    pumpLocaleQueue();
  };
  const icon = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.7 4.6a5.4 5.4 0 0 0-7.6 0L12 5.7l-1.1-1.1a5.4 5.4 0 0 0-7.6 7.6L12 21l8.7-8.8a5.4 5.4 0 0 0 0-7.6Z');
    svg.append(path);
    return svg;
  };
  const renderPage = () => {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    const grid = page.querySelector('.cellini-wishlist__grid, [data-wishlist-items]');
    const empty = page.querySelector('.cellini-wishlist__empty, [data-wishlist-empty]');
    if (!grid || !empty) return;
    const focusedArticle = document.activeElement?.closest?.('.cellini-wishlist-item');
    const focusedHandle = grid.contains(focusedArticle) ? focusedArticle.dataset.wishlistHandle : '';
    const focusedKind = focusedHandle && (document.activeElement.classList.contains('cellini-wishlist-item__remove') ? 'remove' : document.activeElement.classList.contains('cellini-wishlist-item__photo') ? 'photo' : 'title');
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      const shown = localizedItems.get(item.handle) || item;
      const article = document.createElement('article');
      article.className = 'cellini-wishlist-item';
      article.dataset.wishlistHandle = item.handle;
      const url = productURL(page, item);
      const photo = document.createElement('a');
      photo.className = 'cellini-wishlist-item__photo';
      photo.href = url;
      photo.style.setProperty('--cellini-wishlist-ratio', String(shown.ratio));
      photo.setAttribute('aria-label', `${labels.view}: ${shown.title}`);
      if (shown.image) {
        const image = document.createElement('img');
        image.src = shown.image;
        image.alt = shown.title;
        image.loading = 'lazy';
        image.addEventListener('error', () => {
          photo.classList.add('cellini-wishlist-item__photo--empty');
          photo.textContent = 'CELLINI';
        }, { once: true });
        photo.append(image);
      } else {
        photo.classList.add('cellini-wishlist-item__photo--empty');
        photo.textContent = 'CELLINI';
      }
      article.append(photo);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cellini-wishlist-item__remove';
      remove.dataset.wishlistRemove = item.handle;
      remove.setAttribute('aria-label', `${labels.remove}: ${shown.title}`);
      remove.title = labels.remove;
      remove.append(icon());
      article.append(remove);
      if (shown.type) {
        const type = document.createElement('p');
        type.className = 'cellini-wishlist-item__type';
        type.textContent = shown.type;
        article.append(type);
      }
      const heading = document.createElement('h2');
      const link = document.createElement('a');
      link.href = url;
      link.textContent = shown.title || item.handle;
      heading.append(link);
      article.append(heading);
      if (shown.color || shown.price) {
        const meta = document.createElement('div');
        meta.className = 'cellini-wishlist-item__meta';
        if (shown.color) { const color = document.createElement('span'); color.textContent = shown.color; meta.append(color); }
        if (shown.price) { const price = document.createElement('span'); price.textContent = shown.price; meta.append(price); }
        article.append(meta);
      }
      fragment.append(article);
      queueLocale(page, item);
    }
    grid.replaceChildren(fragment);
    grid.hidden = items.length === 0;
    empty.hidden = items.length > 0;
    if (focusedHandle) {
      const replacement = grid.querySelector(`[data-wishlist-handle="${focusedHandle}"]`);
      replacement?.querySelector(focusedKind === 'remove' ? '.cellini-wishlist-item__remove' : focusedKind === 'photo' ? '.cellini-wishlist-item__photo' : 'h2 a')?.focus();
    }
  };
  const render = () => { renderControls(); renderPage(); };
  const toggle = button => {
    const record = buttonRecord(button);
    if (!record) return;
    if (saved(record.handle)) {
      items = items.filter(item => item.handle !== record.handle);
      localizedItems.delete(record.handle);
      status.textContent = labels.deleted;
    } else {
      items = [record, ...items.filter(item => item.handle !== record.handle)].slice(0, limit);
      status.textContent = labels.saved;
    }
    save();
    render();
  };
  const init = () => {
    document.body.append(status);
    render();
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (button) { event.preventDefault(); toggle(button); return; }
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        const page = remove.closest('[data-wishlist-page]');
        const index = [...page.querySelectorAll('[data-wishlist-remove]')].indexOf(remove);
        items = items.filter(item => item.handle !== remove.dataset.wishlistRemove);
        save();
        status.textContent = labels.deleted;
        render();
        const remaining = page.querySelectorAll('[data-wishlist-remove]');
        (remaining[Math.min(index, remaining.length - 1)] || page.querySelector('.cellini-wishlist__empty a'))?.focus();
      }
    });
    window.addEventListener('storage', event => { if (event.key === key) { items = read(); for (const handle of localizedItems.keys()) if (!saved(handle)) localizedItems.delete(handle); render(); } });
    window.addEventListener('pageshow', event => { if (event.persisted) { items = read(); render(); } });
    document.addEventListener('shopify:section:load', render);
    let pending = false;
    new MutationObserver(mutations => {
      if (!mutations.some(mutation => [...mutation.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-wishlist-toggle], [data-wishlist-count]') || node.querySelector?.('[data-wishlist-toggle], [data-wishlist-count]'))))) return;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; renderControls(); });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
